import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Paperclip, Trash2, FileText, Loader2 } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { download } from "./ui";

const Thumb = ({ f }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    if (f.content_type?.startsWith("image/")) {
      api.get(`/admin/files/${f.id}/download`, { responseType: "blob" }).then((r) => { url = URL.createObjectURL(r.data); setSrc(url); }).catch(() => {});
    }
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [f.id, f.content_type]);
  return src ? <img src={src} alt={f.filename} className="h-20 w-full object-cover" /> : <div className="grid h-20 place-items-center bg-white/5"><FileText className="text-zinc-400" /></div>;
};

export const Files = ({ ownerType, ownerId, testid = "files" }) => {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const load = async () => {
    try {
      const { data } = await api.get("/admin/files", { params: { owner_type: ownerType, owner_id: ownerId } });
      setFiles(Array.isArray(data) ? data : []);
    } catch (e) { toast.error(errorText(e)); }
  };
  useEffect(() => { if (ownerId) load(); }, [ownerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (list) => {
    setBusy(true);
    for (const file of list) {
      const fd = new FormData();
      fd.append("owner_type", ownerType);
      fd.append("owner_id", ownerId);
      fd.append("file", file);
      try { await api.post("/admin/files", fd); } catch (e) { toast.error(`${file.name}: ${errorText(e)}`); }
    }
    setBusy(false);
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Fjerne filen?")) return;
    await api.delete(`/admin/files/${id}`);
    load();
  };
  if (!ownerId) return <p className="text-sm text-zinc-400">Lagre først for å legge til bilder og dokumenter.</p>;
  return (
    <div data-testid={testid}>
      <input ref={input} type="file" multiple hidden onChange={(e) => { upload([...e.target.files]); e.target.value = ""; }} data-testid={`${testid}-input`} />
      <button type="button" onClick={() => input.current?.click()} disabled={busy} data-testid={`${testid}-upload`}
        className="flex items-center gap-2 border border-dashed border-white/25 px-4 py-3 font-mono text-xs uppercase tracking-widest text-zinc-300 hover:border-white/50">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Paperclip size={14} />} Last opp bilder / dokumenter (maks 15 MB)
      </button>
      <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {files.map((f) => (
          <div key={f.id} className="border border-white/10" data-testid={`${testid}-item`}>
            <button type="button" onClick={() => download(`/admin/files/${f.id}/download`, f.filename)} className="block w-full"><Thumb f={f} /></button>
            <div className="flex items-center justify-between gap-1 p-2">
              <span className="truncate text-xs text-zinc-300" title={f.filename}>{f.filename}</span>
              <button type="button" onClick={() => remove(f.id)} aria-label="Fjern fil" className="text-zinc-500 hover:text-red-400"><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
